"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Calendar, Clock, Phone } from "lucide-react";
import { IComingSoonProduct } from "@/app/types/product.types";
import { PALETTE } from "@/app/lib/colorPallete";
interface ComingSoonProductCardProps {
  product: IComingSoonProduct;
  onCardClick: (product: IComingSoonProduct) => void;
}

const ComingSoonProductCard: React.FC<ComingSoonProductCardProps> = ({
  product,
  onCardClick,
}) => {
  const formatExpectedDate = (date?: Date) => {
    if (!date) return "Coming Soon";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const handleClick = () => {
    onCardClick(product);
  };

  return (
    <motion.div
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="group relative bg-gradient-to-br from-stone-800/40 to-stone-900/40 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border border-amber-500/20 hover:border-amber-500/60 transition-all duration-300 cursor-pointer h-full flex flex-col"
      onClick={handleClick}
      role="button"
      aria-label={`View details for ${product.modelName}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      { }
      <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-3 py-1 mt-1 rounded-full bg-yellow-500/90 backdrop-blur-sm border border-rose-300/50">
        <Phone size={14} className="text-white" />
        <span className="text-[10px] font-bold text-white uppercase tracking-wider">
          Contact Us for info
        </span>
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-lg backdrop-blur-md bg-white border border-white/30">
        <Image
          src="/logo-tsp.png"
          alt="Logo"
          width={200}
          height={200}
          className="opacity-90 w-20 h-5"
        />
        {

        }
      </div>

      { }
      <div className="relative h-56 bg-gradient-to-br from-stone-700/30 to-stone-800/30 flex items-center justify-center overflow-hidden">
        {product.commonImageUrls && product.commonImageUrls.length > 0 ? (
          <div className="relative w-full h-full">
            <Image
              src={product.commonImageUrls[0]}
              alt={product.modelName}
              fill
              className="object-cover group-hover:scale-110 transition-transform duration-500"
              sizes="(max-width: 768px) 100vw, 33vw"
              priority={false}
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-stone-500">
            <Clock size={48} className="mb-2 opacity-30" />
            <span className="text-sm font-medium opacity-50">No preview</span>
          </div>
        )}
      </div>

      { }
      <div className="p-5 relative z-0 flex-grow flex flex-col">
        <h3
          className="font-serif text-xl font-bold mb-2 line-clamp-2 group-hover:text-amber-400 transition-colors"
          style={{ color: PALETTE.cream }}
        >
          {product.modelName}
        </h3>

        {product.subtext && (
          <p
            className="text-sm mb-4 line-clamp-2"
            style={{ color: PALETTE.lightGray }}
          >
            {product.subtext}
          </p>
        )}

        <div className="flex items-center gap-2 mb-4">
          {

          }
          {

          }
        </div>

        {product.expectedDate && (
          <div className="flex items-center gap-2 text-sm mt-auto pt-4">
            <Calendar size={16} style={{ color: PALETTE.gold }} />
            <span style={{ color: PALETTE.lightGray }}>
              Expected:{" "}
              <strong style={{ color: PALETTE.cream }}>
                {formatExpectedDate(product.expectedDate)}
              </strong>
            </span>
          </div>
        )}
      </div>

      { }
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${PALETTE.gold}20 0%, transparent 50%, ${PALETTE.gold}20 100%)`,
        }}
      />
    </motion.div>
  );
};

export default ComingSoonProductCard;
